import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, Loader2, Save } from 'lucide-react';
import { CenteredLoader } from '../CenteredLoader';
import { NoProjectSelectedState } from '../NoProjectSelectedState';
import { PageErrorBanner } from '../PageErrorBanner';
import { RuleSwitch } from '../red-flag-rules/RuleSwitch';
import PreJourneyBaseCitiesModal from './PreJourneyBaseCitiesModal';
import { preJourneyPlanAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import {
  PLAN_CADENCES,
  PLAN_PRIORITIES,
  PLAN_WHERES,
  type PlanCadence,
  type PlanPriority,
  type PlanRule,
  type PlanRules,
  type PlanSettingsResponse,
  type PlanWhere,
} from '../../types/preJourneyPlan';

/**
 * Master Data → Store Visits Configuration → Pre-Journey Plan.
 *
 * Which roles get a monthly visit plan, and the visit rules it is built with. A planned
 * person's stores (the ones they handle in Store Visits) are inside their base city or
 * upcountry, and each gets one priority, first match wins: New iPro, PIP, Red flags, Score
 * declining. The rule for that priority and place sets how many visits are due and how often.
 * A rule switched off is skipped: the store falls through to the next priority.
 *
 * Nothing ticked is the plan switched off. One Save sends the roles and all eight rules; the
 * server then rebuilds this month's plan (past months keep the plan they were built with).
 * Base cities are reviewed in their own window (PreJourneyBaseCitiesModal), saved on their own.
 */

/** Asked by the page before a tab switch leaves unsaved edits behind. */
export const DISCARD_PLAN_PROMPT = 'Discard your unsaved changes to the Pre-Journey Plan?';

interface PreJourneyPlanTabProps {
  /** Where the page's tab row wants this tab's Save button. */
  actionSlot: HTMLElement | null;
  /** Told whenever the tab gains or loses unsaved edits, so leaving the tab can ask first. */
  onDirtyChange?: (dirty: boolean) => void;
}

const VISITS_MIN = 1;
const VISITS_MAX = 10;
const VISITS_ERROR = `Enter a whole number from ${VISITS_MIN} to ${VISITS_MAX}.`;

const PRIORITY_LABEL: Record<PlanPriority, string> = {
  new_ipro: '1 · New iPro',
  pip: '2 · PIP',
  red_flags: '3 · Red flags',
  score_declining: '4 · Score declining',
};
const CADENCE_LABEL: Record<PlanCadence, string> = {
  per_month: 'a month',
  months_1_2: 'in M1 and M2',
  per_quarter: 'a quarter',
};
const WHERE_TITLE: Record<PlanWhere, { title: string; hint: string }> = {
  upcountry: { title: 'Upcountry stores', hint: "Stores outside the AM's base city" },
  inside: { title: 'Inside city stores', hint: "Stores in the AM's base city" },
};

const LOAD_FAILED = 'Could not load the Pre-Journey Plan.';
const SAVE_FAILED = 'Could not save the Pre-Journey Plan.';

const apiMessage = (e: unknown, fallback: string): string => {
  const message = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

/** A rule as edited: the number kept as typed until Save checks it. */
interface DraftRule {
  enabled: boolean;
  visits: string;
  cadence: PlanCadence;
}
type DraftRules = Record<PlanWhere, Record<PlanPriority, DraftRule>>;
interface Draft {
  roleIds: string[];
  rules: DraftRules;
}
type RuleErrors = Partial<Record<`${PlanWhere}.${PlanPriority}`, string>>;

const keyOf = (where: PlanWhere, priority: PlanPriority) => `${where}.${priority}` as const;

const wholeNumber = (text: string): number | null => (/^\d+$/.test(text.trim()) ? Number(text.trim()) : null);
const validVisits = (text: string): number | null => {
  const n = wholeNumber(text);
  return n !== null && n >= VISITS_MIN && n <= VISITS_MAX ? n : null;
};

const mapRules = <A, B>(rules: Record<PlanWhere, Record<PlanPriority, A>>, fn: (rule: A, where: PlanWhere, priority: PlanPriority) => B) =>
  Object.fromEntries(PLAN_WHERES.map((where) => [
    where,
    Object.fromEntries(PLAN_PRIORITIES.map((priority) => [priority, fn(rules[where][priority], where, priority)])),
  ])) as Record<PlanWhere, Record<PlanPriority, B>>;

// The server's defaults, used only for a rule an answer leaves out (it sends all eight).
const DEFAULT_RULE: Record<PlanWhere, Record<PlanPriority, PlanRule>> = {
  upcountry: {
    new_ipro: { enabled: true, visits: 1, cadence: 'months_1_2' },
    pip: { enabled: true, visits: 1, cadence: 'months_1_2' },
    red_flags: { enabled: true, visits: 1, cadence: 'per_quarter' },
    score_declining: { enabled: true, visits: 1, cadence: 'per_quarter' },
  },
  inside: {
    new_ipro: { enabled: true, visits: 2, cadence: 'per_month' },
    pip: { enabled: true, visits: 2, cadence: 'per_month' },
    red_flags: { enabled: true, visits: 2, cadence: 'per_month' },
    score_declining: { enabled: true, visits: 2, cadence: 'per_month' },
  },
};

/** The saved settings as a draft: roles the project no longer has are dropped. */
const toDraft = (data: PlanSettingsResponse): Draft => {
  const known = new Set(data.roles.map((r) => r.id));
  return {
    roleIds: (data.settings.roleIds ?? []).filter((id) => known.has(id)),
    rules: mapRules(DEFAULT_RULE, (fallback, where, priority) => {
      const rule = data.settings.rules?.[where]?.[priority] ?? fallback;
      return { enabled: rule.enabled, visits: String(rule.visits), cadence: rule.cadence };
    }),
  };
};

/** What Save would send, as a string: two drafts with one fingerprint have nothing to save between them. */
const fingerprint = (d: Draft): string => [
  [...d.roleIds].sort().join(','),
  ...PLAN_WHERES.flatMap((where) => PLAN_PRIORITIES.map((priority) => {
    const r = d.rules[where][priority];
    return `${where}.${priority}:${r.enabled ? 'on' : 'off'}:${wholeNumber(r.visits) ?? r.visits.trim()}:${r.cadence}`;
  })),
].join('|');

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-11' → 'November 2026'. */
const monthLabel = (ym: string): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) return ym;
  return `${MONTH_NAMES[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
};

/** A moment as India reads it: '1 Nov, 12:30 am'. Worked out from UTC, so the reader's own time zone cannot move it. */
const indiaTime = (iso: string): string | null => {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms + 330 * 60 * 1000);
  const hours = d.getUTCHours();
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${d.getUTCDate()} ${SHORT_MONTHS[d.getUTCMonth()]}, ${h12}:${minutes} ${hours < 12 ? 'am' : 'pm'}`;
};

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

const CARD = 'rounded-lg border border-gray-100 bg-white p-6';
const LABEL = 'text-sm font-semibold text-gray-900';
const FIELD = 'rounded-lg border px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50';

export default function PreJourneyPlanTab({ actionSlot, onDirtyChange }: PreJourneyPlanTabProps) {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);

  // The project whose plan is on screen; it trails a project switch by one render, so an
  // answer still on its way for the old project can be told apart.
  const [projectId, setProjectId] = useState(selectedProjectId);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // The server's answer (roles, month, build time, the two warnings) and the settings as saved and as edited.
  const [info, setInfo] = useState<PlanSettingsResponse | null>(null);
  const [baseline, setBaseline] = useState<Draft | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<RuleErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showOver, setShowOver] = useState(false);
  const [citiesOpen, setCitiesOpen] = useState(false);
  const [refusals, setRefusals] = useState(0);
  const rulesRef = useRef<HTMLDivElement>(null);
  const shownProject = useRef(projectId);
  shownProject.current = projectId;

  const dirty = baseline !== null && draft !== null && fingerprint(draft) !== fingerprint(baseline);

  useEffect(() => {
    setProjectId(selectedProjectId);
  }, [selectedProjectId]);

  const applyAnswer = (data: PlanSettingsResponse) => {
    const next = toDraft(data);
    setInfo(data);
    setBaseline(next);
    setDraft(next);
    setErrors({});
  };

  useEffect(() => {
    setInfo(null);
    setBaseline(null);
    setDraft(null);
    setErrors({});
    setLoadError(null);
    setSaveError(null);
    setNotice(null);
    setShowOver(false);
    setCitiesOpen(false);
    if (!projectId) {
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    preJourneyPlanAPI
      .getSettings(projectId)
      .then((res) => {
        if (!alive) return;
        const data = res.data?.data as PlanSettingsResponse | undefined;
        if (!data || !data.settings || !Array.isArray(data.roles)) {
          setLoadError(LOAD_FAILED);
          return;
        }
        applyAnswer(data);
      })
      .catch((e) => alive && setLoadError(apiMessage(e, LOAD_FAILED)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [projectId]);

  // Leaving the page (reload / close tab) with unsaved edits asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (refusals > 0) rulesRef.current?.querySelector<HTMLInputElement>('input[aria-invalid="true"]')?.focus();
  }, [refusals]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const toggleRole = (roleId: string) => {
    setNotice(null);
    setDraft((d) => (d ? {
      ...d,
      roleIds: d.roleIds.includes(roleId) ? d.roleIds.filter((id) => id !== roleId) : [...d.roleIds, roleId],
    } : d));
  };

  const editRule = (where: PlanWhere, priority: PlanPriority, change: Partial<DraftRule>) => {
    setNotice(null);
    setDraft((d) => (d ? {
      ...d,
      rules: { ...d.rules, [where]: { ...d.rules[where], [priority]: { ...d.rules[where][priority], ...change } } },
    } : d));
    // A refusal belongs to what the box held (or to a rule that was on); it is checked again on Save.
    if ('visits' in change || change.enabled === false) {
      setErrors((prev) => {
        const key = keyOf(where, priority);
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  /** After a base-city save: the plan was rebuilt, so the build time and the two warnings are read again. Edits on screen stay. */
  const refreshInfo = async () => {
    if (!projectId) return;
    try {
      const res = await preJourneyPlanAPI.getSettings(projectId);
      const data = res.data?.data as PlanSettingsResponse | undefined;
      if (shownProject.current !== projectId || !data || !data.settings) return;
      setInfo(data);
    } catch {
      // The figures stay as they were; the next visit to the tab reads them again.
    }
  };

  const save = async () => {
    if (!projectId || !dirty || saving || !draft || !baseline) return;
    const found: RuleErrors = {};
    for (const where of PLAN_WHERES) {
      for (const priority of PLAN_PRIORITIES) {
        const r = draft.rules[where][priority];
        if (r.enabled && validVisits(r.visits) === null) found[keyOf(where, priority)] = VISITS_ERROR;
      }
    }
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setRefusals((n) => n + 1);
      return;
    }

    // A rule switched off still carries its numbers (the server keeps all eight); one whose
    // box was left half-typed keeps the number it was saved with.
    const rules: PlanRules = mapRules(draft.rules, (r, where, priority) => ({
      enabled: r.enabled,
      visits: validVisits(r.visits) ?? validVisits(baseline.rules[where][priority].visits) ?? VISITS_MIN,
      cadence: r.cadence,
    }));

    setSaving(true);
    setSaveError(null);
    setNotice(null);
    try {
      // In the project's role order, not the order they were ticked, so the same choice always reads the same.
      const roleIds = (info?.roles ?? []).map((r) => r.id).filter((id) => draft.roleIds.includes(id));
      const res = await preJourneyPlanAPI.saveSettings(projectId, { roleIds, rules });
      if (shownProject.current !== projectId) return;
      const data = res.data?.data as PlanSettingsResponse | undefined;
      if (data && data.settings && Array.isArray(data.roles)) applyAnswer(data);
      else setBaseline(draft);
      // When the rebuild failed the save still stands; the warning line says the rest.
      setNotice(draft.roleIds.length === 0 ? 'Saved. The plan is off for this project.'
        : data?.buildError ? 'Saved.' : "Saved. This month's plan was rebuilt.");
    } catch (e) {
      if (shownProject.current === projectId) setSaveError(apiMessage(e, SAVE_FAILED));
    } finally {
      setSaving(false);
    }
  };

  if (!selectedProjectId) {
    return <NoProjectSelectedState message="Select a project from the dropdown in the menu bar." />;
  }
  if (loading) return <CenteredLoader />;
  if (loadError || !info || !draft) return <PageErrorBanner message={loadError ?? LOAD_FAILED} />;

  const built = info.builtAt ? indiaTime(info.builtAt) : null;
  const missing = info.missingBaseCity ?? 0;
  const over = info.overCapacity ?? [];

  const ruleRow = (where: PlanWhere, priority: PlanPriority) => {
    const rule = draft.rules[where][priority];
    const error = errors[keyOf(where, priority)];
    const errorId = `pre-journey-${where}-${priority}-error`;
    const label = `${PRIORITY_LABEL[priority].replace(/^\d · /, '')}, ${WHERE_TITLE[where].title.toLowerCase()}`;
    return (
      <li key={priority} className="py-3">
        <div className="flex flex-wrap items-center gap-3">
          <RuleSwitch
            checked={rule.enabled}
            onChange={(next) => editRule(where, priority, { enabled: next })}
            label={label}
            disabled={saving}
          />
          <span className={`min-w-[9rem] flex-1 text-sm ${rule.enabled ? 'text-gray-900' : 'text-gray-400'}`}>{PRIORITY_LABEL[priority]}</span>
          <input
            type="number"
            min={VISITS_MIN}
            max={VISITS_MAX}
            step={1}
            inputMode="numeric"
            aria-label={`Visits for ${label}`}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            value={rule.visits}
            disabled={saving || !rule.enabled}
            onChange={(e) => editRule(where, priority, { visits: e.target.value })}
            className={`w-20 ${FIELD} ${error ? 'border-red-400' : 'border-gray-300'}`}
          />
          <select
            aria-label={`How often for ${label}`}
            value={rule.cadence}
            disabled={saving || !rule.enabled}
            onChange={(e) => editRule(where, priority, { cadence: e.target.value as PlanCadence })}
            className={`${FIELD} border-gray-300 bg-white text-gray-900`}
          >
            {PLAN_CADENCES.map((c) => <option key={c} value={c}>{CADENCE_LABEL[c]}</option>)}
          </select>
        </div>
        {error && <p id={errorId} className="mt-1 text-xs text-red-700">{error}</p>}
      </li>
    );
  };

  return (
    <div className="space-y-6">
      {actionSlot && createPortal(
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Save className="h-4 w-4" aria-hidden />}
          Save
        </button>,
        actionSlot,
      )}

      {notice && (
        <div role="status" className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <CheckCircle className="h-4 w-4 flex-shrink-0" aria-hidden />
          {notice}
        </div>
      )}
      {saveError && <PageErrorBanner message={saveError} />}

      <section className={CARD} aria-label="Who gets a plan">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className={LABEL}>Plan is on for</h2>
            {info.roles.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">This project has no roles yet.</p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {info.roles.map((role) => {
                  const on = draft.roleIds.includes(role.id);
                  return (
                    <button
                      key={role.id}
                      type="button"
                      aria-pressed={on}
                      disabled={saving}
                      onClick={() => toggleRole(role.id)}
                      className={`rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 ${
                        on ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-primary-200'
                      }`}
                    >
                      {role.name}
                    </button>
                  );
                })}
              </div>
            )}
            {draft.roleIds.length === 0 && info.roles.length > 0 && (
              <p className="mt-2 text-xs text-gray-500">Nothing ticked: the plan is off.</p>
            )}
          </div>
          <p className="text-sm text-gray-600">
            Plan for <span className="font-medium text-gray-900">{monthLabel(info.planMonth)}</span>
            {' · '}
            {built ? `built ${built}` : 'not built yet'}
          </p>
        </div>
        <p className="mt-4 text-sm text-gray-600">
          Base city of each AM: from their 1Office position.{' '}
          {missing > 0 ? `${missing.toLocaleString()} ${plural(missing, 'has', 'have')} none.` : 'Everyone on the plan has one.'}{' '}
          <button
            type="button"
            onClick={() => setCitiesOpen(true)}
            className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
          >
            Review base cities
          </button>
        </p>
      </section>

      <div ref={rulesRef} className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {PLAN_WHERES.map((where) => (
          <section key={where} className={CARD} aria-label={WHERE_TITLE[where].title}>
            <h2 className={LABEL}>{WHERE_TITLE[where].title}</h2>
            <p className="mt-1 text-xs text-gray-500">{WHERE_TITLE[where].hint}</p>
            <ul className="mt-2 divide-y divide-gray-100">
              {PLAN_PRIORITIES.map((priority) => ruleRow(where, priority))}
            </ul>
          </section>
        ))}
      </div>

      {info.buildError && (
        <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {info.buildError}
        </p>
      )}

      {over.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p>
            {over.length.toLocaleString()} {plural(over.length, 'person', 'people')} would need more visits this month than their visit targets allow.{' '}
            <button
              type="button"
              aria-expanded={showOver}
              onClick={() => setShowOver((v) => !v)}
              className="font-medium underline hover:text-amber-900"
            >
              {showOver ? 'Hide' : 'See who'}
            </button>
          </p>
          {showOver && (
            <ul className="mt-2 space-y-1">
              {over.map((p) => (
                <li key={p.employeeCode}>
                  {p.name || p.employeeCode} ({p.employeeCode}) · {p.due.toLocaleString()} due · target allows {p.capacity.toLocaleString()}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <p className="text-xs text-gray-500">Saving rebuilds this month&apos;s plan. Past months keep the plan they had.</p>

      {citiesOpen && projectId && (
        <PreJourneyBaseCitiesModal
          projectId={projectId}
          onClose={() => setCitiesOpen(false)}
          onSaved={(_changed, buildError) => {
            setCitiesOpen(false);
            setNotice(buildError ? 'Base cities saved.' : "Base cities saved. This month's plan was rebuilt.");
            void refreshInfo();
          }}
        />
      )}
    </div>
  );
}
